import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_GROUPCONTRACT_LOAD_BY_GROUP_ID,
    ACTION_GROUPCONTRACT_LOAD_BY_ID,
    ACTION_GROUPCONTRACT_LOAD_FAILURE,
    ACTION_GROUPCONTRACT_LOAD_SUCCESS,

    ACTION_GROUPCONTRACT_SAVE,
    ACTION_GROUPCONTRACT_SAVE_SUCCESS,
    ACTION_GROUPCONTRACT_SAVE_FAILURE,

    ACTION_GROUPCONTRACT_VALIDATE,
    ACTION_GROUPCONTRACT_VALIDATE_SUCCESS,
    ACTION_GROUPCONTRACT_VALIDATE_FAILURE,

    ACTION_GROUPCONTRACT_CREATE,
    ACTION_GROUPCONTRACT_CREATE_SUCCESS,
    ACTION_GROUPCONTRACT_CREATE_FAILURE,

    ACTION_GROUPCONTRACT_SYNC,
    ACTION_GROUPCONTRACT_SYNC_SUCCESS,
    ACTION_GROUPCONTRACT_SYNC_FAILURE,

    ACTION_GROUPCONTRACT_CLEAR,
    ACTION_GROUPCONTRACT_FILTER,

    GroupContractAction
} from '../actions/groupContract';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IGroupContractState {
    formData: Dtos.GroupContract | Dtos.GroupContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.GroupContract[];
    formLength: number;
}

const initialState: IGroupContractState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IGroupContractState, _isLoading: boolean): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IGroupContractState,
    formData: Dtos.GroupContract | Dtos.GroupContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IGroupContractState, responseStatus: Dtos.ResponseStatus): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IGroupContractState, formFilter: IFormFilter): IGroupContractState {

    const items: Dtos.GroupContract[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.agreementName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: IGroupContractState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IGroupContractState, propertiesToClear?: string[]): IGroupContractState {

    if (!propertiesToClear) {
        const newState: IGroupContractState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IGroupContractState, _isSaving: boolean): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IGroupContractState,
    formData: Dtos.GroupContract | Dtos.GroupContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IGroupContractState, responseStatus: Dtos.ResponseStatus): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IGroupContractState, _isValidating: boolean): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IGroupContractState,
    formData: Dtos.GroupContract | Dtos.GroupContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IGroupContractState, responseStatus: Dtos.ResponseStatus): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IGroupContractState, _isCreating: boolean): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IGroupContractState,
    formData: Dtos.GroupContract,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IGroupContractState, responseStatus: Dtos.ResponseStatus): IGroupContractState {
    const newState: IGroupContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const GroupContractReducer: Reducer<IGroupContractState> = (state: IGroupContractState = initialState, action: GroupContractAction) => {
    switch (action.type) {
        case ACTION_GROUPCONTRACT_LOAD_BY_GROUP_ID:
        case ACTION_GROUPCONTRACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_GROUPCONTRACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPCONTRACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_GROUPCONTRACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_GROUPCONTRACT_SAVE:
            return saving(state, true);
        case ACTION_GROUPCONTRACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPCONTRACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_GROUPCONTRACT_VALIDATE:
            return validating(state, true);
        case ACTION_GROUPCONTRACT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPCONTRACT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_GROUPCONTRACT_CREATE:
            return creating(state, true);
        case ACTION_GROUPCONTRACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_GROUPCONTRACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_GROUPCONTRACT_FILTER:
            return filter(state, action.formFilter);
        case ACTION_GROUPCONTRACT_SYNC:
            return loading(state, true);
        case ACTION_GROUPCONTRACT_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPCONTRACT_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default GroupContractReducer;